import type { Decor } from '../decor';
import type { BuildingLot, CityLayout } from '../types';
import { ChunkedBuilders, hash01, type RGB } from './meshBuilder';
import { PAVE_Y, WATER_Y } from './ground';
import { T, tileUV, type SignAtlas } from './textures';

/**
 * The big set pieces: the railway viaduct with its arches and iron spans, the canal bridges, the
 * gas holders, dockside cranes, a moored coaster, the scrap yard stacks — and a ring of backdrop
 * streets beyond the boundary so the district never ends in a void.
 */

const WHITE = tileUV(T.white, 40);
const IRON: RGB = [0.1, 0.11, 0.11];
const RUST: RGB = [0.45, 0.18, 0.1];
/**
 * Running rails: rusty web and foot, a worn head. Drawn with the `props` material, not `metal`: the
 * metal's tight sun highlight along a 6–8 cm rail head (and its pale vertex colour) read as a bright
 * white line from the high camera by day and night.
 */
const RAIL_SIDE: RGB = [0.07, 0.052, 0.042];
const RAIL_HEAD: RGB = [0.12, 0.115, 0.11];
/** The crane rails lie flush in the quay apron, open to the whole sky: a darker, greasy head. */
const CRANE_RAIL_HEAD: RGB = [0.085, 0.08, 0.075];
/**
 * Viaduct sleepers: creosoted timber gone black with oil and soot, a shade apart one from the next.
 * They were a fresh-sawn tan (`[0.22, 0.17, 0.13]` on `metal`), whose upward face read as pale planks
 * from the high camera; they now share the rails' rough `props` material.
 */
const SLEEPER_TONES: RGB[] = [
  [0.05, 0.038, 0.03],
  [0.06, 0.046, 0.035],
  [0.045, 0.036, 0.03],
  [0.068, 0.052, 0.04],
];
const sleeper = (x: number, z: number): RGB => SLEEPER_TONES[Math.floor(hash01(Math.round(x * 3 + 7), Math.round(z * 10)) * SLEEPER_TONES.length)]!;

export function buildLandmarks(layout: CityLayout, decor: Decor, cb: ChunkedBuilders, signs: SignAtlas): void {
  viaduct(decor, cb, signs);
  bridges(layout, decor, cb);
  for (const g of decor.gasholders) gasholder(cb, g.center.x, g.center.z, g.radius, g.height);
  for (const c of decor.cranes) crane(cb, c.pos.x, c.pos.z, c.heading);
  for (const s of decor.ships) ship(cb, signs, s.pos.x, s.pos.z, s.heading, s.length, s.name);
  for (const p of decor.scrapPiles) scrapPile(cb, p.center.x, p.center.z, p.halfX, p.halfZ, p.height);
  for (const s of decor.stacks) stack(cb, signs, s);
  // dock rails along the basin quay (dark rusty steel with a worn head: a pale grey under the open sky
  // reflected off the metal material as a white line from the high camera)
  const pb = cb.get('props', 160, 211);
  for (const z of [207.8, 214.2]) pb.box(200, PAVE_Y + 0.005, z, 0.12, 0.06, 200, Math.PI / 2, RAIL_SIDE, WHITE, { topColour: CRANE_RAIL_HEAD });
}

// ------------------------------------------------------------------------------------- viaduct

function viaduct(decor: Decor, cb: ChunkedBuilders, signs: SignAtlas): void {
  const V = decor.viaduct;
  const x0 = V.x - V.halfWidth;
  const x1 = V.x + V.halfWidth;
  const top = V.deckHeight;
  const bayLen = 8.5;
  const tint: RGB = [0.82, 0.74, 0.7];
  // baked contact shading (docs/POLISH.md, "Graphics batch 2"): the arch faces darken towards the
  // pavement, the brick band under the deck's parapet a touch at the top
  const foot: RGB = [tint[0] * 0.8, tint[1] * 0.8, tint[2] * 0.8];
  let archNo = 1;
  // Arched spans.
  for (const s of V.spans) {
    const len = s.z1 - s.z0;
    const n = Math.max(1, Math.round(len / bayLen));
    for (let i = 0; i < n; i++) {
      const za = s.z0 + (len * i) / n;
      const zb = s.z0 + (len * (i + 1)) / n;
      const fb = cb.get('facade', V.x, (za + zb) / 2);
      const h = hash01(i, Math.round(za));
      const west = h < 0.18 ? T.archWorkshopLit : h < 0.7 ? T.archLockup : T.archBlank;
      const east = hash01(i, Math.round(za), 3) < 0.5 ? T.archLockup : T.archBlank;
      // west face (normal -x) walks +z; east face (normal +x) walks -z
      fb.quad([x0, PAVE_Y, za], [x0, PAVE_Y, zb], [x0, top - 1.5, zb], [x0, top - 1.5, za], tileUV(west), foot, tint);
      fb.quad([x1, PAVE_Y, zb], [x1, PAVE_Y, za], [x1, top - 1.5, za], [x1, top - 1.5, zb], tileUV(east), foot, tint);
      // upper band (plain brick) up to the deck
      fb.quad([x0, top - 1.5, za], [x0, top - 1.5, zb], [x0, top, zb], [x0, top, za], tileUV(T.brickSoot), tint);
      fb.quad([x1, top - 1.5, zb], [x1, top - 1.5, za], [x1, top, za], [x1, top, zb], tileUV(T.brickSoot), tint);
      // pilasters between arches
      fb.box(V.x, PAVE_Y, za, V.halfWidth * 2 + 0.5, top - 0.6, 0.7, 0, [0.7, 0.62, 0.58], tileUV(T.brickSoot), { top: false, baseShade: 0.8 });
      if (west === T.archWorkshopLit || hash01(i, 77, Math.round(za)) < 0.12) {
        // arch number plate
        const sg = signs.add(`ARCH ${archNo}`, 'plate');
        cb.global('signs').quad([x0 - 0.03, 4.6, za + len / n / 2 - 0.8], [x0 - 0.03, 4.6, za + len / n / 2 + 0.8], [x0 - 0.03, 4.95, za + len / n / 2 + 0.8], [x0 - 0.03, 4.95, za + len / n / 2 - 0.8], sg.uv, [1, 1, 1]);
      }
      archNo++;
    }
    const fb = cb.get('facade', V.x, (s.z0 + s.z1) / 2);
    // end walls of the span
    for (const [z, dir] of [
      [s.z0, -1],
      [s.z1, 1],
    ] as const) {
      if (dir < 0) fb.quad([x1, PAVE_Y, z], [x0, PAVE_Y, z], [x0, top, z], [x1, top, z], tileUV(T.brickSoot), foot, tint);
      else fb.quad([x0, PAVE_Y, z], [x1, PAVE_Y, z], [x1, top, z], [x0, top, z], tileUV(T.brickSoot), foot, tint);
    }
  }
  // Openings: iron girder spans over roads, brick arch over the alley, girders over the canal.
  for (const o of V.openings) {
    const fb = cb.get('facade', V.x, (o.z0 + o.z1) / 2);
    if (o.kind === 'arch') {
      const springY = 4.6;
      fb.quad([x0, springY, o.z0], [x0, springY, o.z1], [x0, top, o.z1], [x0, top, o.z0], tileUV(T.brickSoot), tint);
      fb.quad([x1, springY, o.z1], [x1, springY, o.z0], [x1, top, o.z0], [x1, top, o.z1], tileUV(T.brickSoot), tint);
      fb.quadFacing([x0, springY, o.z0], [x1, springY, o.z0], [x1, springY, o.z1], [x0, springY, o.z1], WHITE, [0.12, 0.1, 0.09], [0, -1, 0]);
    } else {
      const gy = top - 2.6;
      const bot = o.kind === 'water' ? WATER_Y : PAVE_Y;
      fb.quad([x0 - 0.3, gy, o.z0], [x0 - 0.3, gy, o.z1], [x0 - 0.3, top + 0.4, o.z1], [x0 - 0.3, top + 0.4, o.z0], tileUV(T.girder), [1, 1, 1]);
      fb.quad([x1 + 0.3, gy, o.z1], [x1 + 0.3, gy, o.z0], [x1 + 0.3, top + 0.4, o.z0], [x1 + 0.3, top + 0.4, o.z1], tileUV(T.girder), [1, 1, 1]);
      fb.quadFacing([x0 - 0.3, gy, o.z0], [x1 + 0.3, gy, o.z0], [x1 + 0.3, gy, o.z1], [x0 - 0.3, gy, o.z1], WHITE, [0.08, 0.08, 0.08], [0, -1, 0]);
      // abutment piers either side
      for (const z of [o.z0, o.z1]) fb.box(V.x, bot, z, V.halfWidth * 2 + 1.0, top - bot + 0.2, 1.0, 0, [0.62, 0.58, 0.54], tileUV(T.stonePlain), { baseShade: 0.8 });
      if (o.kind === 'girder') {
        const sg = signs.add('KESSMOUTH & DISTRICT RAILWAY', 'painted');
        const zm = (o.z0 + o.z1) / 2;
        const w = Math.min(o.z1 - o.z0 - 1, 14);
        const sb = cb.global('signs');
        sb.quad([x0 - 0.35, gy + 0.7, zm - w / 2], [x0 - 0.35, gy + 0.7, zm + w / 2], [x0 - 0.35, gy + 0.7 + w / sg.aspect, zm + w / 2], [x0 - 0.35, gy + 0.7 + w / sg.aspect, zm - w / 2], sg.uv, [1, 1, 1]);
        sb.quad([x1 + 0.35, gy + 0.7, zm + w / 2], [x1 + 0.35, gy + 0.7, zm - w / 2], [x1 + 0.35, gy + 0.7 + w / sg.aspect, zm - w / 2], [x1 + 0.35, gy + 0.7 + w / sg.aspect, zm + w / 2], sg.uv, [1, 1, 1]);
      }
    }
  }
  // Deck: ballast, sleepers, rails, parapets, along the full length.
  const z0 = -760;
  const z1 = 760;
  for (let z = z0; z < z1; z += 20) {
    const zb = Math.min(z + 20, z1);
    const fb = cb.get('facade', V.x, (z + zb) / 2);
    const rb = cb.get('props', V.x, (z + zb) / 2);
    fb.quadFacing([x0, top, z], [x1, top, z], [x1, top, zb], [x0, top, zb], tileUV(T.felt), [0.75, 0.68, 0.6], [0, 1, 0]);
    // parapet walls
    fb.box(x0 + 0.2, top, (z + zb) / 2, 0.4, 1.1, zb - z, 0, tint, tileUV(T.brickSoot), { uvTop: WHITE, topColour: [0.24, 0.23, 0.22] });
    fb.box(x1 - 0.2, top, (z + zb) / 2, 0.4, 1.1, zb - z, 0, tint, tileUV(T.brickSoot), { uvTop: WHITE, topColour: [0.24, 0.23, 0.22] });
    // two tracks
    for (const tx of [V.x - 2, V.x + 2]) {
      for (let s = z + 0.4; s < zb; s += 0.9) rb.box(tx, top + 0.02, s, 2.4, 0.1, 0.24, 0, sleeper(tx, s), WHITE, { bottom: false });
      for (const rx of [-0.72, 0.72]) rb.box(tx + rx, top + 0.12, (z + zb) / 2, 0.08, 0.14, zb - z, 0, RAIL_SIDE, WHITE, { topColour: RAIL_HEAD });
    }
  }
  // A stabled goods train on the east track, and signal posts.
  const gb = cb.get('props', V.x, -130);
  const wagonCols: RGB[] = [
    [0.32, 0.16, 0.1],
    [0.2, 0.22, 0.22],
    [0.36, 0.2, 0.12],
  ];
  for (let i = 0; i < 9; i++) {
    const zc = -175 + i * 8.6;
    gb.box(V.x + 2, top + 0.25, zc, 2.6, 0.5, 7.8, 0, IRON, WHITE);
    gb.box(V.x + 2, top + 0.75, zc, 2.7, 2.2, 7.6, 0, wagonCols[i % 3]!, i % 3 === 1 ? tileUV(T.corrugatedRoof) : tileUV(T.timber));
  }
  gb.box(V.x + 2, top + 0.25, -175 - 9, 2.7, 3.2, 9, 0, [0.05, 0.08, 0.07], WHITE);
  for (const z of [-260, -40, 120, 260]) {
    const mb = cb.get('metal', V.x, z);
    const glow = cb.get('glow', V.x, z);
    mb.cylinder(x1 - 0.6, z, top, top + 5.5, 0.1, 0.08, 6, IRON, WHITE);
    mb.box(x1 - 0.6, top + 4.6, z + 0.2, 0.5, 1.0, 0.2, 0, IRON, WHITE);
    glow.box(x1 - 0.6, top + 5.0, z + 0.32, 0.2, 0.2, 0.05, 0, hash01(Math.round(z)) < 0.5 ? [4, 0.4, 0.3] : [0.4, 3.5, 1.2], WHITE);
  }
}

// ------------------------------------------------------------------------------------- bridges

function bridges(layout: CityLayout, decor: Decor, cb: ChunkedBuilders): void {
  for (const br of decor.bridges) {
    const e = layout.roads.edges[br.edgeId]!;
    const bx = layout.roads.nodes[e.from]!.pos.x;
    const hw = e.width / 2 + e.sidewalk + 0.5;
    const fb = cb.get('facade', bx, (br.z0 + br.z1) / 2);
    const iron = e.name === 'Gullet Bridge';
    // side fascias down to the water, arch soffit
    const z0 = br.z0;
    const z1 = br.z1;
    if (iron) {
      fb.quad([bx - hw, -1.6, z0], [bx - hw, -1.6, z1], [bx - hw, PAVE_Y + 0.05, z1], [bx - hw, PAVE_Y + 0.05, z0], tileUV(T.girder), [0.9, 1.0, 0.95]);
      fb.quad([bx + hw, -1.6, z1], [bx + hw, -1.6, z0], [bx + hw, PAVE_Y + 0.05, z0], [bx + hw, PAVE_Y + 0.05, z1], tileUV(T.girder), [0.9, 1.0, 0.95]);
    } else {
      const n = 4;
      for (let i = 0; i < n; i++) {
        const a = z0 + ((z1 - z0) * i) / n;
        const b = z0 + ((z1 - z0) * (i + 1)) / n;
        // (darker down at the waterline, where the bridge sits in its own shadow)
        fb.quad([bx - hw, WATER_Y - 0.5, a], [bx - hw, WATER_Y - 0.5, b], [bx - hw, PAVE_Y + 0.05, b], [bx - hw, PAVE_Y + 0.05, a], tileUV(i === 1 || i === 2 ? T.archBlank : T.brickSoot), [0.6, 0.54, 0.51], [0.8, 0.72, 0.68]);
        fb.quad([bx + hw, WATER_Y - 0.5, b], [bx + hw, WATER_Y - 0.5, a], [bx + hw, PAVE_Y + 0.05, a], [bx + hw, PAVE_Y + 0.05, b], tileUV(i === 1 || i === 2 ? T.archBlank : T.brickSoot), [0.6, 0.54, 0.51], [0.8, 0.72, 0.68]);
      }
    }
  }
}

// ------------------------------------------------------------------------------------- gas holder

function gasholder(cb: ChunkedBuilders, cx: number, cz: number, r: number, h: number): void {
  const mb = cb.get('metal', cx, cz);
  const pb = cb.get('props', cx, cz);
  const cols = 14;
  const frameR = r + 0.8;
  const frame: RGB = [0.16, 0.24, 0.22];
  for (let i = 0; i < cols; i++) {
    const a = (i / cols) * Math.PI * 2;
    mb.cylinder(cx + Math.cos(a) * frameR, cz + Math.sin(a) * frameR, PAVE_Y, h, 0.38, 0.32, 6, frame, WHITE);
  }
  // horizontal girder rings
  for (const ry of [h * 0.36, h * 0.68, h - 0.4]) {
    for (let i = 0; i < cols; i++) {
      const a0 = (i / cols) * Math.PI * 2;
      const a1 = ((i + 1) / cols) * Math.PI * 2;
      const xa = cx + Math.cos(a0) * frameR;
      const za = cz + Math.sin(a0) * frameR;
      const xb = cx + Math.cos(a1) * frameR;
      const zb = cz + Math.sin(a1) * frameR;
      const len = Math.hypot(xb - xa, zb - za);
      mb.box((xa + xb) / 2, ry, (za + zb) / 2, 0.25, 0.7, len, Math.atan2(xb - xa, zb - za), frame, WHITE);
      // lattice cross bracing between rings
      if (ry < h - 1) {
        const y2 = Math.min(ry + h * 0.32, h - 0.4);
        mb.beam([xa, ry + 0.3, za], [xb, y2, zb], 0.12, [0.12, 0.18, 0.17], WHITE);
        mb.beam([xb, ry + 0.3, zb], [xa, y2, za], 0.12, [0.12, 0.18, 0.17], WHITE);
      }
    }
  }
  // the bell (tank), partly risen; ribbed and rust-streaked, with the riveted plate seams showing
  const bellH = h * 0.62;
  pb.cylinder(cx, cz, PAVE_Y, bellH, r, r, 28, [0.15, 0.18, 0.16], tileUV(T.corrugated), { top: false });
  for (let ry = bellH * 0.33; ry < bellH - 0.5; ry += bellH * 0.33) pb.cylinder(cx, cz, ry, ry + 0.16, r + 0.06, r + 0.06, 28, [0.1, 0.12, 0.11], WHITE, { top: false });
  // the crown: a shallow dome with a lighter weathered centre and a vent in the middle
  pb.cylinder(cx, cz, bellH, bellH + 1.6, r, r * 0.25, 28, [0.09, 0.1, 0.1], WHITE, { topColour: [0.11, 0.115, 0.11] });
  pb.cylinder(cx, cz, bellH + 1.6, bellH + 2.4, 0.5, 0.45, 8, [0.14, 0.14, 0.14], WHITE);
  pb.cylinder(cx, cz, bellH - 0.3, bellH + 0.1, r + 0.1, r + 0.1, 28, RUST, WHITE, { top: false });
  // rust running down the plates from the seams (HIGH and MEDIUM)
  const rust = cb.get('clutter', cx, cz);
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2 + hash01(i, Math.round(cx), 131) * 0.4;
    const y0 = bellH * (0.3 + hash01(i, Math.round(cx), 132) * 0.6);
    const len = 1 + hash01(i, Math.round(cx), 133) * 3;
    const px = cx + Math.cos(a) * (r + 0.03);
    const pz = cz + Math.sin(a) * (r + 0.03);
    rust.box(px, y0 - len, pz, 0.2 + hash01(i, 3, 134) * 0.3, len, 0.02, -a + Math.PI / 2, [0.3, 0.13, 0.06], WHITE);
  }
  // ladder / walkway
  mb.box(cx + r + 0.4, PAVE_Y, cz, 0.6, bellH + 1.2, 0.5, 0, IRON, WHITE);
}

// ------------------------------------------------------------------------------------- cranes

function crane(cb: ChunkedBuilders, x: number, z: number, heading: number): void {
  const mb = cb.get('metal', x, z);
  const glow = cb.get('glow', x, z);
  const rust = cb.get('clutter', x, z);
  // Red oxide paint, chalked and faded a shade differently on every member, rusting from the feet up.
  const seed = Math.round(x * 3 + z * 7);
  const paint = (k: number): RGB => {
    const f = 0.8 + hash01(seed, k, 121) * 0.3;
    return [0.5 * f, 0.17 * f + 0.02, 0.1 * f];
  };
  const col = paint(0);
  const dark: RGB = [0.12, 0.12, 0.13];
  const portalH = 8;
  // four legs, rust-brown for the first two metres where the salt gets them
  let k = 1;
  for (const [lx, lz] of [
    [-3.4, -3.2],
    [3.4, -3.2],
    [-3.4, 3.2],
    [3.4, 3.2],
  ] as const) {
    mb.box(x + lx, PAVE_Y, z + lz, 0.8, 2.2, 0.8, 0, [0.3, 0.14, 0.07], WHITE);
    mb.box(x + lx, PAVE_Y + 2.2, z + lz, 0.8, portalH - 2.2, 0.8, 0, paint(k++), WHITE);
    // rust streaks running down from the bolted joints (HIGH and MEDIUM)
    for (let i = 0; i < 2; i++) {
      const sx = x + lx + (hash01(seed, k * 3 + i, 122) - 0.5) * 0.6;
      const h0 = 2.6 + hash01(seed, k * 3 + i, 123) * 4;
      rust.box(sx, PAVE_Y + h0, z + lz + 0.42, 0.12, 0.6 + hash01(seed, k * 3 + i, 124) * 1.4, 0.02, 0, [0.28, 0.12, 0.05], WHITE);
      rust.box(sx, PAVE_Y + h0, z + lz - 0.42, 0.12, 0.6 + hash01(seed, k * 3 + i, 125) * 1.4, 0.02, 0, [0.28, 0.12, 0.05], WHITE);
    }
  }
  mb.box(x, portalH, z, 7.6, 1.0, 7.4, 0, paint(9), WHITE);
  // slewing superstructure
  const cs = Math.cos(heading);
  const sn = Math.sin(heading);
  const P = (lx: number, lz: number): [number, number] => [x + lx * cs + lz * sn, z - lx * sn + lz * cs];
  const [hx, hz] = P(0, -0.5);
  mb.box(hx, portalH + 1, hz, 4.2, 4.2, 5.5, heading, paint(10), WHITE);
  mb.box(hx, portalH + 5.2, hz, 3.8, 0.4, 5.0, heading, dark, WHITE);
  // the driver's cab roof is sooted from the diesel exhaust, with a stack beside it
  mb.box(hx, portalH + 5.6, hz, 2.4, 0.08, 3.2, heading, [0.08, 0.08, 0.08], WHITE);
  mb.cylinder(hx, hz, portalH + 5.2, portalH + 7.2, 0.14, 0.14, 6, [0.1, 0.1, 0.1], WHITE);
  const [wx, wz] = P(0, 1.6);
  glow.box(wx, portalH + 2.6, wz, 3.0, 1.2, 2.4, heading, [2.2, 1.8, 1.2], WHITE);
  const [cwx, cwz] = P(0, -4.2);
  mb.box(cwx, portalH + 1.5, cwz, 3.6, 2.4, 2.0, heading, dark, WHITE);
  // jib: a triangular lattice boom rising toward the water
  const jibLen = 30;
  const rise = 0.42;
  const J = (t: number, side: number, up: number): [number, number, number] => {
    const along = 2.5 + jibLen * Math.cos(rise) * t;
    const w = 1.1 * (1 - t * 0.6) * side;
    const [px, pz] = P(w, along);
    return [px, portalH + 3 + jibLen * Math.sin(rise) * t + up * (1.4 * (1 - t * 0.7)), pz];
  };
  const segs = 10;
  const bw = 0.16;
  mb.beam(J(0, -1, 0), J(1, -1, 0), bw * 1.4, col, WHITE);
  mb.beam(J(0, 1, 0), J(1, 1, 0), bw * 1.4, col, WHITE);
  mb.beam(J(0, 0, 1), J(1, 0, 1), bw * 1.4, col, WHITE);
  for (let i = 0; i < segs; i++) {
    const t0 = i / segs;
    const t1 = (i + 1) / segs;
    mb.beam(J(t0, -1, 0), J(t1, 0, 1), bw, col, WHITE);
    mb.beam(J(t0, 1, 0), J(t1, 0, 1), bw, col, WHITE);
    mb.beam(J(t0, -1, 0), J(t1, 1, 0), bw, col, WHITE);
  }
  // luffing ties back to the A-frame
  const [ax, az] = P(0, -1.5);
  mb.beam([ax, portalH + 9.5, az], J(0.55, 0, 1), 0.08, dark, WHITE);
  mb.beam([ax, portalH + 9.5, az], [hx, portalH + 5.4, hz], 0.3, col, WHITE);
  // portal leg bracing
  for (const [ax0, az0, ax1, az1] of [
    [-3.4, -3.2, 3.4, -3.2],
    [-3.4, 3.2, 3.4, 3.2],
  ] as const) {
    mb.beam([x + ax0, PAVE_Y + 1, z + az0], [x + ax1, portalH - 0.5, z + az1], 0.2, col, WHITE);
    mb.beam([x + ax1, PAVE_Y + 1, z + az1], [x + ax0, portalH - 0.5, z + az0], 0.2, col, WHITE);
  }
  // hoist rope and hook block hanging from the jib head
  const tip = P(0, 2.5 + jibLen * Math.cos(rise));
  const tipY = portalH + 3 + jibLen * Math.sin(rise);
  mb.cylinder(tip[0], tip[1], tipY - 14, tipY, 0.04, 0.04, 4, dark, WHITE, { top: false });
  mb.box(tip[0], tipY - 15, tip[1], 0.6, 1.0, 0.6, heading, [0.75, 0.6, 0.15], WHITE);
}

// ------------------------------------------------------------------------------------- ship

function ship(cb: ChunkedBuilders, signs: SignAtlas, x: number, z: number, heading: number, length: number, name: string): void {
  const pb = cb.get('props', x, z);
  const glow = cb.get('glow', x, z);
  const cs = Math.cos(heading);
  const sn = Math.sin(heading);
  // local: +Z = bow direction
  const P = (lx: number, y: number, lz: number): [number, number, number] => [x + lx * cs + lz * sn, y, z - lx * sn + lz * cs];
  const beam = 10;
  const hullTop = 1.8;
  const hullBot = WATER_Y - 0.5;
  const hullC: RGB = [0.08, 0.09, 0.1];
  const boot: RGB = [0.42, 0.1, 0.08];
  const L = length / 2;
  // hull outline (stern square, bow pointed)
  const outline: [number, number][] = [
    [-beam / 2, -L],
    [beam / 2, -L],
    [beam / 2, L - 10],
    [beam * 0.3, L - 4],
    [0, L],
    [-beam * 0.3, L - 4],
    [-beam / 2, L - 10],
  ];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const pa = P(a[0], hullBot, a[1]);
    const pbb = P(b[0], hullBot, b[1]);
    const ta = P(a[0], hullTop, a[1]);
    const tb = P(b[0], hullTop, b[1]);
    const nx = b[1] - a[1];
    const nz = -(b[0] - a[0]);
    const dir: [number, number, number] = [nx * cs + nz * sn, 0, -nx * sn + nz * cs];
    pb.quadFacing(pa, pbb, tb, ta, WHITE, hullC, dir);
    const wa = P(a[0], WATER_Y + 0.6, a[1]);
    const wb = P(b[0], WATER_Y + 0.6, b[1]);
    pb.quadFacing(P(a[0], WATER_Y - 0.1, a[1]), P(b[0], WATER_Y - 0.1, b[1]), wb, wa, WHITE, boot, dir);
  }
  pb.polygon(
    outline.map(([lx, lz]) => {
      const p = P(lx, hullTop, lz);
      return { x: p[0], z: p[2] };
    }),
    hullTop,
    [0.32, 0.27, 0.22],
  );
  // hatches
  for (let i = 0; i < 3; i++) {
    const c = P(0, hullTop, -L + 22 + i * 12);
    pb.box(c[0], hullTop, c[2], 6.5, 0.9, 9, heading, [0.2, 0.32, 0.3], tileUV(T.corrugatedRoof));
  }
  // bridge superstructure at the stern
  const s0 = P(0, hullTop, -L + 7);
  pb.box(s0[0], hullTop, s0[2], beam - 1.5, 3.0, 9, heading, [0.82, 0.8, 0.74], WHITE);
  pb.box(s0[0], hullTop + 3, s0[2], beam - 3, 2.6, 6, heading, [0.82, 0.8, 0.74], WHITE);
  const wl = P(0, hullTop + 4.1, -L + 9.9);
  glow.box(wl[0], hullTop + 3.8, wl[2], beam - 3.4, 0.6, 0.3, heading, [2.5, 2.1, 1.4], WHITE);
  const fn = P(0, 0, -L + 4.5);
  pb.cylinder(fn[0], fn[2], hullTop + 5.6, hullTop + 9.5, 1.0, 0.9, 10, [0.75, 0.55, 0.12], WHITE, { topColour: [0.05, 0.05, 0.05] });
  pb.cylinder(fn[0], fn[2], hullTop + 8.3, hullTop + 9.0, 0.97, 0.92, 10, [0.08, 0.08, 0.08], WHITE, { top: false });
  // masts with derricks
  for (const mz of [-L + 16, L - 14]) {
    const m = P(0, 0, mz);
    pb.cylinder(m[0], m[2], hullTop, hullTop + 12, 0.22, 0.15, 6, [0.5, 0.45, 0.3], WHITE);
    glow.box(m[0], hullTop + 12, m[2], 0.25, 0.25, 0.25, 0, [3, 3, 2.6], WHITE);
  }
  // name on the bow quarter
  const sg = signs.add(name, 'painted');
  const bw = 9;
  for (const side of [-1, 1]) {
    const a = P((side * beam) / 2 + side * 0.03, hullTop - 1.4, L - 16 - (side * bw) / 2);
    const b = P((side * beam) / 2 + side * 0.03, hullTop - 1.4, L - 16 + (side * bw) / 2);
    const c = [b[0], hullTop - 1.4 + bw / sg.aspect, b[2]] as [number, number, number];
    const d = [a[0], hullTop - 1.4 + bw / sg.aspect, a[2]] as [number, number, number];
    cb.global('signs').quad(a, b, c, d, sg.uv, [1, 1, 1]);
  }
}

// ------------------------------------------------------------------------------------- dock stacks

function stack(cb: ChunkedBuilders, signs: SignAtlas, s: Decor['stacks'][number]): void {
  const pb = cb.get('props', s.center.x, s.center.z);
  const cs = Math.cos(s.angle);
  const sn = Math.sin(s.angle);
  if (s.kind === 'timber') {
    // sawn timber in banded bundles on bearers
    for (let t = 0; t < 3; t++) {
      for (const lz of [-0.7, 0.7]) {
        const x = s.center.x + lz * sn;
        const z = s.center.z + lz * cs;
        pb.box(x, PAVE_Y + t * 0.75, z, s.halfX * 2 - t * 0.4, 0.7, 1.3, s.angle, [0.55 - t * 0.03, 0.42, 0.27], tileUV(T.timber));
      }
    }
    return;
  }
  const cols: RGB[] = [
    [0.45, 0.16, 0.1],
    [0.12, 0.25, 0.36],
    [0.2, 0.32, 0.22],
    [0.55, 0.42, 0.14],
    [0.32, 0.32, 0.3],
  ];
  const names = ['MERROW', 'CORRAN', 'KESTREL LINE', 'GULLET'];
  const h = 2.6;
  for (let t = 0; t < s.tiers; t++) {
    const c = cols[(s.seed * 3 + t * 2) % cols.length]!;
    pb.box(s.center.x, PAVE_Y + t * h, s.center.z, s.halfX * 2, h - 0.02, s.halfZ * 2, s.angle, c, tileUV(T.corrugated));
    // painted line name on the long side facing the camera-ish (+z side when angle 0)
    if (t === s.tiers - 1 || t === 0) {
      const sg = signs.add(names[(s.seed + t) % names.length]!, 'painted');
      const w = Math.min(s.halfX * 2 - 1, 7);
      const y0 = PAVE_Y + t * h + 0.9;
      const hh = w / sg.aspect;
      const ox = s.halfZ + 0.03;
      const P = (lx: number, y: number): [number, number, number] => [s.center.x + lx * cs + ox * sn, y, s.center.z - lx * sn + ox * cs];
      cb.global('signs').quad(P(-w / 2, y0), P(w / 2, y0), P(w / 2, y0 + hh), P(-w / 2, y0 + hh), sg.uv, [1, 1, 1]);
    }
  }
}

// ------------------------------------------------------------------------------------- scrap

function scrapPile(cb: ChunkedBuilders, cx: number, cz: number, hx: number, hz: number, h: number): void {
  const pb = cb.get('props', cx, cz);
  const cols: RGB[] = [
    [0.42, 0.18, 0.12],
    [0.25, 0.3, 0.36],
    [0.5, 0.42, 0.25],
    [0.3, 0.32, 0.26],
    [0.55, 0.52, 0.48],
    [0.2, 0.16, 0.14],
  ];
  const layers = Math.max(1, Math.round(h / 0.62));
  let k = 0;
  for (let ly = 0; ly < layers; ly++) {
    const inset = ly * 0.3;
    for (let x = -hx + 1.1 + inset; x < hx - 1 - inset; x += 2.15) {
      for (let z = -hz + 2.1 + inset; z < hz - 2 - inset; z += 4.2) {
        if (hash01(k++, ly, Math.round(cx)) < 0.12) continue;
        const c = cols[Math.floor(hash01(k, 7, Math.round(cz)) * cols.length)]!;
        const rot = (hash01(k, 11) - 0.5) * 0.25;
        pb.box(cx + x, PAVE_Y + ly * 0.62, cz + z, 1.9, 0.6, 3.9, rot, c, WHITE);
      }
    }
  }
}

// ------------------------------------------------------------------------------------- backdrop

/** Rows of buildings outside the playable boundary (visual only), facing inwards. */
export function backdropLots(layout: CityLayout): BuildingLot[] {
  const out: BuildingLot[] = [];
  const B = layout.bounds;
  let id = 100000;
  const styles: BuildingLot['style'][] = ['terrace', 'warehouse', 'flats', 'terrace', 'office', 'shopfront', 'factory'];
  const add = (cx: number, cz: number, halfX: number, halfZ: number, angle: number, k: number): void => {
    // keep the estuary (south-east) and canal mouths open
    if (cx > 92 && cz > 205) return;
    if (cz > 136 && cz < 189 && (cx < -290 || cx > 290)) return;
    if (Math.abs(cx + 120) < 9) return; // the viaduct runs out through the backdrop
    const style = styles[Math.floor(hash01(k, 5) * styles.length)]!;
    const height = style === 'flats' ? 14 + hash01(k, 2) * 12 : style === 'warehouse' ? 12 + hash01(k, 3) * 8 : 7 + hash01(k, 4) * 6;
    const tags = style === 'terrace' ? [`units:${Math.max(2, Math.round((halfX * 2) / 5.2))}`] : style === 'factory' ? ['sawtooth'] : [];
    out.push({ id: id++, center: { x: cx, z: cz }, halfX, halfZ, angle, height, style, tags, districtId: 'backdrop' });
  };
  let k = 0;
  for (const ring of [0, 1, 2, 3]) {
    const off = 6 + ring * 27;
    // north (front faces +z), south (front faces -z), west (faces +x), east (faces -x)
    for (let x = B.minX - 40; x < B.maxX + 40; ) {
      const w = 12 + hash01(k, 1) * 22;
      const d = 10 + hash01(k, 6) * 10;
      add(x + w / 2, B.minZ - off - d / 2, w / 2, d / 2, 0, k++);
      add(x + w / 2, B.maxZ + off + d / 2, w / 2, d / 2, Math.PI, k++);
      x += w + 1 + hash01(k, 9) * 3;
    }
    for (let z = B.minZ - 10; z < B.maxZ + 10; ) {
      const w = 12 + hash01(k, 1) * 22;
      const d = 10 + hash01(k, 6) * 10;
      add(B.minX - off - d / 2, z + w / 2, w / 2, d / 2, Math.PI / 2, k++);
      add(B.maxX + off + d / 2, z + w / 2, w / 2, d / 2, -Math.PI / 2, k++);
      z += w + 1 + hash01(k, 9) * 3;
    }
  }
  return out;
}
